import React, { useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Button } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { getUiInterface } from "../../../locales/translationUtils";
import { useGeneric } from "../../../apis/generic/GenericProvider";
import getFilterLabels from "../../../types/filterLabels";

interface FilterProps {
    filters: Record<string, string>;
    onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSituationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch: () => void;
}

const Filter: React.FC<FilterProps> = ({ filters, onFilterChange, onSituationChange, onSearch }) => {
    const { t } = useTranslation();
    const filterLabels = getFilterLabels();
    const selectRefs = useRef<(HTMLSelectElement | null)[]>([]);
    const { distinctOptions, fetchDistinctOptions } = useGeneric();

    const selectOptions = {
        situation: ["Pending", "Released", "Shipped"],
        from: distinctOptions["wareHouse"] || [],
        client: distinctOptions["client"] || [],
    };

    const filterDataLabel = new Map();
    filterDataLabel.set("Pending", t('report.fields.pu'));
    filterDataLabel.set("Released", t('report.fields.released'));
    filterDataLabel.set("Shipped", t('report.fields.shipDate'));

    useEffect(() => {
        fetchDistinctOptions('wareHouse');
        fetchDistinctOptions('client');
    }, [fetchDistinctOptions]);

    return (
        <Form className="mb-3">
            <Row className="mb-2">
                {Object.entries(filters)
                    .filter(([key]) => !["situationDateInicio", "situationDateFim", "paginated", "pageNum", "quantityPerPage"].includes(key))
                    .map(([key, value], index) => (

                        <Col md={2} key={key}>
                            <Form.Group controlId={`filter-${key}`} className="position-relative">
                            <Form.Label>{filterLabels[key] || key.charAt(0).toUpperCase() + key.slice(1)}</Form.Label>

                                {selectOptions[key as keyof typeof selectOptions] ? (
                                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                        <Form.Control
                                            as="select"
                                            name={key}
                                            value={value}
                                            ref={(el: HTMLSelectElement | null) => {
                                                if (el) selectRefs.current[index] = el;
                                            }}
                                            onChange={(key !== "situation") ? onFilterChange : onSituationChange}
                                            style={{ appearance: "none", paddingRight: "30px", cursor: "pointer" }}
                                        >
                                            <option value="">
                                                {key === 'situation' ? t('report.labels.selectSituation') : 
                                                 key === 'from' ? t('report.labels.selectFrom') : 
                                                 key === 'client' ? t('report.labels.selectClient') : 
                                                 `Select ${key}`}
                                            </option>
                                            {selectOptions[key as keyof typeof selectOptions]?.map(
                                                (option) => {
                                                    if (typeof option === "object" && "id" in option && "description" in option) {
                                                        return (
                                                            <option
                                                                key={option.id}
                                                                value={option.id}>
                                                                {option.description}
                                                            </option>
                                                        );
                                                    }

                                                    return (
                                                        <option
                                                            key={option}
                                                            value={option}>
                                                            {key === 'situation' ? t(`report.labels.${option.toLowerCase()}`) : option}
                                                        </option>
                                                    );
                                                }
                                            )}
                                        </Form.Control>

                                        <FaChevronDown
                                            style={{
                                                position: "absolute",
                                                right: "10px",
                                                pointerEvents: "auto",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => selectRefs.current[index]?.focus()}
                                        />
                                    </div>
                                ) : (
                                    <Form.Control
                                        type="text"
                                        placeholder={key}
                                        name={key}
                                        value={value}
                                        onChange={onFilterChange}
                                    />
                                )}
                            </Form.Group>
                        </Col>
                    ))}

                <Col md={2}>
                    <Form.Group controlId="from-date">
                        <Form.Label>{t('report.labels.from')} ({filterDataLabel.get(filters.situation)})</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control 
                                type="date"
                                name="situationDateInicio"
                                value={filters.situationDateInicio}
                                onChange={onFilterChange}    
                            />
                        </div>
                    </Form.Group>

                </Col>

                <Col md={2}>
                    <Form.Group controlId="to-date">
                        <Form.Label>{t('report.labels.to')} ({filterDataLabel.get(filters.situation)})</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control 
                                type="date" 
                                name="situationDateFim"
                                value={filters.situationDateFim}
                                onChange={onFilterChange}    
                            />
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <Button variant="primary" onClick={onSearch}>
                        {getUiInterface("search")}
                    </Button>
                </Col>
            </Row>
        </Form>

    );
};

export default Filter;
